import * as THREE from 'three';
import { TEAM } from '../game/MatchAuthority.js';

/**
 * Overhead markers on your own side.
 *
 * Team modes create a failure the game has never had before: shooting the wrong
 * person. Everything else about a soldier — silhouette, animation, weapon, the
 * whole carefully-argued value ladder in `EnemyModel` — is identical on both sides,
 * because it is the same model. A uniform hue shift helps, but it is the *second*
 * signal and cannot be the only one: it disappears in shadow, at range, and for a
 * player who cannot separate rust from olive at all.
 *
 * So allies wear a marker, and it is drawn **through walls**. That sounds like
 * cheating and is the opposite: it removes information the player would otherwise
 * have to guess at (is that shape behind the crate a friend?) while giving away
 * nothing they could act on — you cannot shoot what you cannot see, and the marker
 * tells you nothing about anyone you might want to shoot.
 *
 * Hostiles get nothing. A game that labels the enemy is a game with no reason to
 * look at the map.
 *
 * ## Cost
 *
 * One shared geometry, one shared material, one `Mesh` per pooled slot. Markers do
 * not cast or receive shadows and never enter the depth prepass. At eight allies
 * that is eight tiny unlit draws — small enough that the pool is sized once at boot
 * and never grown.
 */

export const TUNING = {
  /** Metres above the actor's feet. Above a standing helmet, below a doorway. */
  height: 2.25,
  /** Half-width of the chevron in world units at the reference distance. */
  size: 0.17,
  /**
   * Distance the marker is authored at. Beyond it the marker grows with distance so
   * it keeps a constant size on screen; nearer than it, it stops shrinking, or a
   * squadmate stood next to you wears a dot too small to notice.
   */
  refDistance: 14,
  maxScale: 3.4,
  minScale: 0.85,
  /** Fade out past this, so a marker across the whole map is not a beacon. */
  fadeStart: 70,
  fadeEnd: 95,
  poolSize: 12,
  /** ALPHA's colour. Matches the cool end of the ally uniform tint. */
  color: 0x74c7ff,
};

/** A downward chevron: two triangles, drawn flat and billboarded at the camera. */
function chevronGeometry(s) {
  const g = new THREE.BufferGeometry();
  const t = s * 0.42; // stroke thickness
  // Outer edge then inner edge, as a single quad strip folded at the point.
  const v = new Float32Array([
    -s, s * 0.75, 0, 0, -s * 0.55, 0, -s + t, s * 0.75, 0,
    -s + t, s * 0.75, 0, 0, -s * 0.55, 0, 0, -s * 0.55 + t, 0,
    s, s * 0.75, 0, s - t, s * 0.75, 0, 0, -s * 0.55, 0,
    s - t, s * 0.75, 0, 0, -s * 0.55 + t, 0, 0, -s * 0.55, 0,
  ]);
  g.setAttribute('position', new THREE.BufferAttribute(v, 3));
  return g;
}

export class TeamMarkers {
  constructor(ctx) {
    this.ctx = ctx;
    this.enabled = true;

    this.geo = chevronGeometry(TUNING.size);
    /**
     * `depthTest: false` is the whole feature. `depthWrite: false` and a high
     * `renderOrder` keep it from stamping itself into the depth buffer and punching
     * a hole in anything drawn afterwards — including the post stack's own depth
     * reads, which is how a marker like this usually ends up haloing the sky.
     */
    this.mat = new THREE.MeshBasicMaterial({
      color: TUNING.color,
      depthTest: false,
      depthWrite: false,
      transparent: true,
      opacity: 0.9,
      side: THREE.DoubleSide,
      toneMapped: false,
    });

    this.pool = [];
    for (let i = 0; i < TUNING.poolSize; i++) {
      const m = new THREE.Mesh(this.geo, this.mat);
      m.visible = false;
      m.castShadow = false;
      m.receiveShadow = false;
      m.frustumCulled = false;
      m.renderOrder = 998;
      m.matrixAutoUpdate = true;
      ctx.scene.add(m);
      this.pool.push(m);
    }

    // 'fx' rather than 'ui': this is world geometry and has to be placed before the
    // frame is drawn, and 'ui' keeps ticking while paused — markers drifting behind
    // an open pause menu would be the only thing in the scene still moving.
    ctx.engine.registerUpdate(() => this.update(), 'fx');
  }

  update() {
    const { ctx } = this;
    const cam = ctx.camera;
    const match = ctx.match;
    let used = 0;

    if (this.enabled && match) {
      const localTeam = match.teamOf(ctx.player);
      for (const reg of match.actors.values()) {
        if (used >= this.pool.length) break;
        if (reg.dead || reg.kind !== 'bot' || reg.team !== localTeam) continue;
        // Never mark the local player: the one soldier they cannot mistake.
        if (reg.actor === ctx.player) continue;
        const a = reg.actor;
        if (!a.position) continue;

        const m = this.pool[used];
        m.position.set(a.position.x, a.position.y + TUNING.height, a.position.z);
        const d = m.position.distanceTo(cam.position);
        if (d > TUNING.fadeEnd) continue;

        const s = THREE.MathUtils.clamp(
          d / TUNING.refDistance, TUNING.minScale, TUNING.maxScale
        );
        m.scale.setScalar(s);
        m.quaternion.copy(cam.quaternion);
        m.visible = true;
        used++;
      }
    }

    // Opacity is on the shared material, so it is a single value for every marker
    // rather than per-instance. Driving it from the *nearest* marker would make a
    // close ally brighten a distant one; leaving it flat and only fading the far
    // ones by dropping them entirely is the honest version of one shared uniform.
    for (let i = used; i < this.pool.length; i++) this.pool[i].visible = false;
    this.active = used;
  }

  setEnabled(on) {
    this.enabled = !!on;
  }

  dispose() {
    for (const m of this.pool) this.ctx.scene.remove(m);
    this.pool.length = 0;
    this.geo.dispose();
    this.mat.dispose();
  }
}
